import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Move, Check, X } from 'lucide-react';
import { useUpdatePlyPosition } from '@/hooks/usePlybooks';
import { useToast } from '@/hooks/use-toast';
import type { PlybookPly } from '@/types/plybook';
import { PLY_COLORS } from '@/types/plybook';
import { cn } from '@/lib/utils';

interface PlyImageOverlayProps {
  imageUrl: string;
  plies: PlybookPly[];
  plybookId: string;
  selectedPlyId?: string;
  onSelectPly?: (ply: PlybookPly) => void;
}

export function PlyImageOverlay({
  imageUrl,
  plies,
  plybookId,
  selectedPlyId,
  onSelectPly,
}: PlyImageOverlayProps) {
  const { toast } = useToast();
  const updatePosition = useUpdatePlyPosition();
  const [positioningPly, setPositioningPly] = useState<PlybookPly | null>(null);
  const [pendingPosition, setPendingPosition] = useState<{ x: number; y: number } | null>(null);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!positioningPly) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingPosition({ x, y });
  };

  const handleConfirmPosition = async () => {
    if (!positioningPly || !pendingPosition) return;

    try {
      await updatePosition.mutateAsync({
        id: positioningPly.id,
        plybookId,
        position_x: pendingPosition.x,
        position_y: pendingPosition.y,
      });
      toast({ title: 'Posición guardada', description: `Capa #${positioningPly.ply_number} posicionada` });
      setPositioningPly(null);
      setPendingPosition(null);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar la posición', variant: 'destructive' });
    }
  };

  const handleCancelPosition = () => {
    setPositioningPly(null);
    setPendingPosition(null);
  };

  const startPositioning = (ply: PlybookPly) => {
    setPositioningPly(ply);
    setPendingPosition(null);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Posición de Capas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {positioningPly && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="font-medium text-blue-800">
              Posicionando Capa #{positioningPly.ply_number}
            </p>
            <p className="text-blue-600">
              {pendingPosition
                ? 'Haz click en "Confirmar" para guardar o selecciona otra posición'
                : 'Haz click en la imagen donde va esta capa'}
            </p>
            {pendingPosition && (
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={handleConfirmPosition} disabled={updatePosition.isPending}>
                  <Check className="w-3 h-3 mr-1" />
                  Confirmar
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelPosition}>
                  <X className="w-3 h-3 mr-1" />
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        )}

        <div
          className={cn(
            "relative rounded-lg overflow-hidden bg-muted",
            positioningPly && "cursor-crosshair ring-2 ring-blue-500"
          )}
          onClick={handleImageClick}
        >
          <img
            src={imageUrl}
            alt="Plybook"
            className="w-full h-auto"
            draggable={false}
          />

          {plies.map((ply) => {
            const hasPosition = (ply.position_x != null && ply.position_x > 0) || (ply.position_y != null && ply.position_y > 0);
            const isPositioning = positioningPly?.id === ply.id;
            const showAt = isPositioning && pendingPosition
              ? pendingPosition
              : hasPosition
              ? { x: Number(ply.position_x) || 0, y: Number(ply.position_y) || 0 }
              : null;

            if (!showAt) return null;

            const materialType = ply.material_type || 'other';
            const bgColor = ply.color || PLY_COLORS[materialType] || PLY_COLORS.other;

            return (
              <div
                key={ply.id}
                className={cn(
                  "absolute transform -translate-x-1/2 -translate-y-1/2 transition-all",
                  isPositioning && "animate-pulse scale-125"
                )}
                style={{
                  left: `${showAt.x}%`,
                  top: `${showAt.y}%`,
                }}
              >
                <div
                  className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg border-2 border-white",
                    selectedPlyId === ply.id && "ring-2 ring-offset-2 ring-primary"
                  )}
                  style={{ backgroundColor: bgColor }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!positioningPly) onSelectPly?.(ply);
                  }}
                >
                  {ply.ply_number}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">Capas sin posicionar:</p>
          <div className="flex flex-wrap gap-1">
            {plies
              .filter((ply) => (!ply.position_x || ply.position_x === 0) && (!ply.position_y || ply.position_y === 0))
              .map((ply) => {
                const materialType = ply.material_type || 'other';
                const bgColor = ply.color || PLY_COLORS[materialType] || PLY_COLORS.other;
                const isPositioning = positioningPly?.id === ply.id;

                return (
                  <Button
                    key={ply.id}
                    size="sm"
                    variant={isPositioning ? "default" : "outline"}
                    className="h-7 px-2 text-xs"
                    onClick={() => startPositioning(ply)}
                  >
                    <div
                      className="w-4 h-4 rounded-full mr-1"
                      style={{ backgroundColor: bgColor }}
                    />
                    #{ply.ply_number}
                    <Move className="w-3 h-3 ml-1" />
                  </Button>
                );
              })}
            {plies.filter((ply) => (!ply.position_x || ply.position_x === 0) && (!ply.position_y || ply.position_y === 0)).length === 0 && (
              <span className="text-xs text-muted-foreground italic">Todas las capas están posicionadas</span>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xs text-muted-foreground font-medium">Capas posicionadas:</p>
          <div className="flex flex-wrap gap-1">
            {plies
              .filter((ply) => (ply.position_x != null && ply.position_x > 0) || (ply.position_y != null && ply.position_y > 0))
              .map((ply) => {
                const materialType = ply.material_type || 'other';
                const bgColor = ply.color || PLY_COLORS[materialType] || PLY_COLORS.other;
                const isPositioning = positioningPly?.id === ply.id;

                return (
                  <Button
                    key={ply.id}
                    size="sm"
                    variant={isPositioning ? "default" : "secondary"}
                    className="h-7 px-2 text-xs"
                    onClick={() => startPositioning(ply)}
                  >
                    <div
                      className="w-4 h-4 rounded-full mr-1"
                      style={{ backgroundColor: bgColor }}
                    />
                    #{ply.ply_number}
                    <Move className="w-3 h-3 ml-1" />
                  </Button>
                );
              })}
            {plies.filter((ply) => (ply.position_x != null && ply.position_x > 0) || (ply.position_y != null && ply.position_y > 0)).length === 0 && (
              <span className="text-xs text-muted-foreground italic">Ninguna capa posicionada aún</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
